import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";
import { company } from "../mock";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const onDark = location.pathname === "/" && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/80 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center group">
          <div className={`relative h-14 flex items-center rounded-lg px-3 py-1 transition-all duration-300 ${
            onDark ? "bg-white shadow-md" : "bg-transparent"
          } group-hover:scale-[1.03]`}>
            <img
              src="https://img1.wsimg.com/isteam/ip/0ed9d86f-1518-4c8b-9bd3-8703aae28eb3/blob-cb4c291.png/:/rs=h:100,cg:true,m/qt=q:95"
              alt="Global Tech Solutions"
              className="h-11 w-auto object-contain"
            />
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => {
            const active = location.pathname === l.to;
            let linkClass;
            if (active) {
              linkClass = onDark ? "text-white" : "text-sky-700";
            } else {
              linkClass = onDark ? "text-slate-300 hover:text-white" : "text-slate-700 hover:text-sky-700";
            }
            return (
              <Link
                key={l.to}
                to={l.to}
                className={`relative px-4 py-2 text-sm font-medium rounded-md transition-colors ${linkClass}`}
              >
                {l.label}
                {active && (
                  <span className={`absolute left-4 right-4 -bottom-0.5 h-0.5 rounded-full ${onDark ? "bg-sky-400" : "bg-sky-600"}`} />
                )}
              </Link>
            );
          })}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a
            href={`tel:${company.phone}`}
            className={`flex items-center gap-2 text-sm font-medium transition-colors ${
              onDark ? "text-slate-300 hover:text-white" : "text-slate-700 hover:text-sky-700"
            }`}
          >
            <Phone className="h-4 w-4" />
            {company.phone}
          </a>
          <Link to="/contact">
            <Button className="bg-sky-500 hover:bg-sky-600 text-white rounded-md group">
              Get a Quote
              <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Button>
          </Link>
        </div>

        <button
          className={`lg:hidden p-2 rounded-md ${onDark ? "text-white" : "text-slate-800"}`}
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200 shadow-sm">
          <div className="px-6 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <Link key={l.to} to={l.to} className="py-2 text-slate-800 font-medium">
                {l.label}
              </Link>
            ))}
            <Link to="/contact" className="mt-2">
              <Button className="w-full bg-sky-500 hover:bg-sky-600 text-white">
                Get a Quote
              </Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
